import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';

import {
  AttachUnitDto,
  BuildingDto,
  BuildingSection,
  CreateBuildingDto,
  UnitDefaultSection,
  UpdateBuildingDto,
} from '../dtos/building.dto.interface';
import { buildingsQueryKey, getBuildingQueryKey } from '../queries/building.query';
import { getBuildingUnitsQueryKey, portfolioQueryKey } from '../queries/portfolio.query';
import buildingService from '../services/building.service';

/** The caller shows the errors: a rejected field belongs on the form, not in a toast */
export const useCreateBuilding = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (payload: CreateBuildingDto) => buildingService.create(payload),
    onSuccess() {
      queryCache.invalidateQueries({ key: [buildingsQueryKey] }).catch(() => undefined);
    },
  });
});

/** A new unit shows on its building's page and in the building's counts */
export const useCreateUnit = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (payload: { buildingId: string; unitNumber: string }) =>
      buildingService.createUnit(payload.buildingId, payload.unitNumber),
    onSuccess(_unit, payload) {
      for (const key of [
        getBuildingUnitsQueryKey(payload.buildingId),
        getBuildingQueryKey(payload.buildingId),
        [buildingsQueryKey],
      ]) {
        queryCache.invalidateQueries({ key }).catch(() => undefined);
      }
    },
  });
});

/** A home filed in or out of a building leaves the standing homes and joins or leaves the building's units and counts */
function invalidateUnits(queryCache: ReturnType<typeof useQueryCache>, buildingId: string) {
  for (const key of [
    getBuildingUnitsQueryKey(buildingId),
    getBuildingQueryKey(buildingId),
    [buildingsQueryKey],
    [portfolioQueryKey],
  ]) {
    queryCache.invalidateQueries({ key }).catch(() => undefined);
  }
}

/** The caller shows the errors: a taken number belongs on its row, not in a toast */
export const useAttachUnits = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (payload: { buildingId: string; units: AttachUnitDto[] }) =>
      buildingService.attachUnits(payload.buildingId, payload.units),
    onSuccess: (_result, payload) => invalidateUnits(queryCache, payload.buildingId),
  });
});

export const useDetachUnit = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (payload: { buildingId: string; propertyId: string }) =>
      buildingService.detachUnit(payload.buildingId, payload.propertyId),
    onSuccess: (_result, payload) => invalidateUnits(queryCache, payload.buildingId),
  });
});

/**
 * The building's own writes answer the building as it now reads, without the drift that only its
 * page's read carries; a name, a year or a cover changes nothing about which units drifted, so
 * the page keeps the drift it has
 */
function withCurrentDrift(building: BuildingDto) {
  return (current: BuildingDto | undefined): BuildingDto => ({ ...building, defaultsDrift: current?.defaultsDrift });
}

/** The answer is the building with its new cover, so its page shows it without asking again; the caller shows the errors */
export const useUploadBuildingCover = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (payload: { buildingId: string; image: File }) =>
      buildingService.uploadCover(payload.buildingId, payload.image),
    onSuccess(building) {
      queryCache.setQueryData(getBuildingQueryKey(building.id), withCurrentDrift(building));
      queryCache.invalidateQueries({ key: [buildingsQueryKey] }).catch(() => undefined);
    },
  });
});

/** The answer is the building as it now reads; the caller shows the errors */
export const useUpdateBuilding = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (payload: { buildingId: string; changes: UpdateBuildingDto }) =>
      buildingService.update(payload.buildingId, payload.changes),
    onSuccess(building) {
      queryCache.setQueryData(getBuildingQueryKey(building.id), withCurrentDrift(building));
      queryCache.invalidateQueries({ key: [buildingsQueryKey] }).catch(() => undefined);
    },
  });
});

/**
 * A section saved on the building reaches every unit, so the building, its units and the
 * properties page (where a renamed unit shows) are all read again
 */
export const useSaveBuildingSection = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (payload: { buildingId: string; section: BuildingSection; values: object }) =>
      buildingService.saveSection(payload.buildingId, payload.section, payload.values),
    onSuccess(_nothing, payload) {
      for (const key of [
        getBuildingQueryKey(payload.buildingId),
        getBuildingUnitsQueryKey(payload.buildingId),
        [buildingsQueryKey],
        [portfolioQueryKey],
      ]) {
        queryCache.invalidateQueries({ key }).catch(() => undefined);
      }
    },
  });
});

/** A default changes what the next unit starts with, nothing on the units already made: only the building is read again */
export const useSaveBuildingUnitDefault = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (payload: { buildingId: string; section: UnitDefaultSection; values: object }) =>
      buildingService.saveUnitDefault(payload.buildingId, payload.section, payload.values),
    onSuccess(_nothing, payload) {
      queryCache
        .invalidateQueries({ key: getBuildingQueryKey(payload.buildingId), exact: true })
        .catch(() => undefined);
    },
  });
});

/** The building is gone from every list it was on; the caller shows the errors and leaves its page */
export const useDeleteBuilding = defineMutation(() => {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: (buildingId: string) => buildingService.remove(buildingId),
    onSuccess() {
      queryCache.invalidateQueries({ key: [buildingsQueryKey] }).catch(() => undefined);
    },
  });
});
